/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useMemo, useState } from "react";
// ANT DESIGN
import { Card, Tooltip, Button, InputNumber, Select, Table, Tag } from "antd";
// FONT AWESOME
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useLazyGetAdapterKeysQuery } from "../../../redux/keys/keysAPI";
import {
  useLazyGetDebounceOverridesQuery,
  usePostDebounceOverrideMutation,
  usePutDebounceOverrideMutation,
  useDeleteDebounceOverrideMutation,
} from "../../../redux/debounceOverrides/debounceOverridesAPI";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { BankInterface } from "../../../interfaces/Bank.interface";
import { DebounceOverrideInterface } from "../../../interfaces/DebounceOverride.interface";
// UTILS
import {
  OverrideRow,
  buildOverrideRequestBody,
  buildOverrideRows,
  computeSaveActions,
  filterOverridesByScope,
} from "./debounceOverrideUtils";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
import { formatUuidT } from "../../../flatbuffers/display";

export default function BanksDebounceOverrides({
  bank,
  isLoading,
}: {
  bank: BankInterface;
  isLoading: boolean;
}) {
  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [selectedLiftId, setSelectedLiftId] = useState<string | null>(null);
  const [tableData, setTableData] = useState<OverrideRow[]>([]);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetKeys, { data: keysResponse, isFetching: isFetchingKeys }] =
    useLazyGetAdapterKeysQuery();
  const [triggerGetOverrides, { data: overridesResponse, isFetching: isFetchingOverrides }] =
    useLazyGetDebounceOverridesQuery();
  const [triggerPost] = usePostDebounceOverrideMutation();
  const [triggerPut] = usePutDebounceOverrideMutation();
  const [triggerDelete] = useDeleteDebounceOverrideMutation();

  // ************************************************ */
  // ALERT STATE ************************************ */
  const [alertState, setAlertState] = useState<{
    isSuccess: boolean;
    isError: boolean;
    error: any;
  }>({ isSuccess: false, isError: false, error: null });

  // ************************************************ */
  // DERIVED DATA *********************************** */
  const adapterKeys: any[] = useMemo(
    () => (keysResponse && keysResponse.data ? keysResponse.data : []),
    [keysResponse],
  );

  const allOverrides: DebounceOverrideInterface[] = useMemo(
    () => (overridesResponse && overridesResponse.data ? overridesResponse.data : []),
    [overridesResponse],
  );

  // Filter overrides for current scope (bank-wide or specific lift)
  const scopedOverrides = useMemo(
    () => filterOverridesByScope(allOverrides, selectedLiftId),
    [allOverrides, selectedLiftId],
  );

  // Bank-level overrides (liftId === null) — used as reference column when viewing lift scope
  const bankLevelOverrides = useMemo(() => allOverrides.filter((o) => !o.liftId), [allOverrides]);

  // ************************************************ */
  // FETCH DATA ************************************* */
  // bank.id/bank.adapterId are UuidT objects, not primitive strings — a
  // parent handing this component a freshly-fetched (but value-identical)
  // bank still produces a new object graph every time, and this effect
  // would fire on every one of those if it depended on the objects
  // directly. Depending on their formatUuidT string form instead — already
  // computed here for the actual API args below — makes the dependency
  // comparison value-based, so an unrelated parent refetch doesn't
  // redundantly reload keys/overrides and discard an in-progress edit.
  const bankIdStr = formatUuidT(bank.id);
  const adapterIdStr = formatUuidT(bank.adapterId);

  const fetchData = useCallback(async () => {
    if (!adapterIdStr) return;
    const token = await GETJwtToken();
    triggerGetKeys({ adapterId: adapterIdStr, token });
    triggerGetOverrides({ bankId: bankIdStr, token });
  }, [adapterIdStr, bankIdStr, triggerGetKeys, triggerGetOverrides]);

  useEffect(() => {
    if (bankIdStr) {
      fetchData();
    }
  }, [bankIdStr, adapterIdStr, fetchData]);

  // ************************************************ */
  // BUILD TABLE DATA ******************************* */
  useEffect(() => {
    setTableData(
      buildOverrideRows(
        adapterKeys,
        scopedOverrides,
        selectedLiftId ? bankLevelOverrides : undefined,
      ),
    );
  }, [adapterKeys, scopedOverrides, selectedLiftId, bankLevelOverrides]);

  // ************************************************ */
  // HANDLERS *************************************** */
  const onOverrideChange = (keyId: string, value: number | null) => {
    setTableData((prev) =>
      prev.map((row) => (row.keyId === keyId ? { ...row, overrideTime: value } : row)),
    );
  };

  const onClickSave = async () => {
    setAlertState({ isSuccess: false, isError: false, error: null });
    const token = await GETJwtToken();

    try {
      const actions = computeSaveActions(tableData, scopedOverrides);
      const promises: Promise<any>[] = [];

      actions.forEach((action) => {
        if (action.type === "POST") {
          promises.push(
            triggerPost({
              token,
              body: buildOverrideRequestBody(bank, action, selectedLiftId),
            }).unwrap(),
          );
        } else if (action.type === "PUT") {
          promises.push(
            triggerPut({
              token,
              id: action.overrideId,
              body: buildOverrideRequestBody(bank, action, selectedLiftId),
            }).unwrap(),
          );
        } else if (action.type === "DELETE") {
          promises.push(triggerDelete({ token, id: action.overrideId }).unwrap());
        }
      });

      await Promise.all(promises);
      setAlertState({ isSuccess: true, isError: false, error: null });

      // Refetch overrides to sync state
      triggerGetOverrides({ bankId: formatUuidT(bank.id), token });
    } catch (err) {
      setAlertState({ isSuccess: false, isError: true, error: err });
    }
  };

  // ************************************************ */
  // TABLE COLUMNS ********************************** */
  const baseColumns = [
    {
      title: "Message",
      dataIndex: "shortMessage",
      key: "shortMessage",
    },
    {
      title: "Device Type",
      dataIndex: "deviceType",
      key: "deviceType",
      render: (val: string) => (val ? <Tag>{val}</Tag> : null),
    },
    {
      title: "Enum ID",
      dataIndex: "enumId",
      key: "enumId",
    },
    {
      title: "Adapter Default (ms)",
      dataIndex: "adapterDefault",
      key: "adapterDefault",
      render: (val: number | null) => <span style={{ color: "gray" }}>{val ?? "—"}</span>,
    },
  ];

  const bankOverrideColumn = {
    title: "Bank Override (ms)",
    dataIndex: "bankOverrideTime",
    key: "bankOverrideTime",
    render: (val: number | null) => (
      <span style={{ color: "gray" }}>{val !== null ? val : "—"}</span>
    ),
  };

  const liftOverrideColumn = {
    title: selectedLiftId ? "Lift Override (ms)" : "Override (ms)",
    dataIndex: "overrideTime",
    key: "overrideTime",
    render: (_: any, record: OverrideRow) => (
      <InputNumber
        min={0}
        step={100}
        value={record.overrideTime}
        onChange={(val) => onOverrideChange(record.keyId, val)}
        placeholder="Use default"
        style={{ width: 120 }}
      />
    ),
  };

  const columns = selectedLiftId
    ? [...baseColumns, bankOverrideColumn, liftOverrideColumn]
    : [...baseColumns, liftOverrideColumn];

  // ************************************************ */
  // LIFT SELECTOR OPTIONS ************************** */
  const liftOptions = useMemo(() => {
    const opts = [{ value: "__bank__", label: "Bank-wide (all lifts)" }];
    if (bank.lifts) {
      bank.lifts.forEach((lift) => {
        opts.push({
          value: formatUuidT(lift.id) || String(lift.id),
          label: lift.name || `Lift ${lift.position}`,
        });
      });
    }
    return opts;
  }, [bank.lifts]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Card
      size="small"
      title={
        <div className="generalStyles__spaceBetween">
          <span>Debounce Overrides</span>
          <div className="generalStyles__flex" style={{ gap: 8 }}>
            <Select
              style={{ width: 220 }}
              value={selectedLiftId ?? "__bank__"}
              onChange={(val) => setSelectedLiftId(val === "__bank__" ? null : val)}
              options={liftOptions}
              loading={isLoading || isFetchingKeys || isFetchingOverrides}
            />
            <Tooltip title="save">
              <Button
                onClick={onClickSave}
                type="primary"
                icon={<FontAwesomeIcon icon={faSave} />}
              />
            </Tooltip>
          </div>
        </div>
      }
      loading={isLoading}
    >
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        size="small"
        locale={{ emptyText: "No debounce-configured keys found" }}
        loading={isFetchingKeys || isFetchingOverrides}
      />

      <GlobalAlert2
        isError={alertState.isError}
        isSuccess={alertState.isSuccess}
        requestType="PUT"
        error={alertState.error}
        name="Debounce Override"
      />
    </Card>
  );
}
